import React, { createContext, useState, useEffect } from 'react';
import { storageService } from '../utils/storageService';

// Create the context
export const AuthContext = createContext();

// Create a provider component
export const AuthProvider = ({ children }) => {
  // State for exhibitor authentication
  const [exhibitorAuth, setExhibitorAuth] = useState({});
  
  // New state for visitor authentication
  const [visitorAuth, setVisitorAuth] = useState(null);
  const [visitorData, setVisitorData] = useState(null);
  
  // Load authentication states on component mount using storageService
  useEffect(() => {
    try {
      // Load exhibitor auth - get all logged in exhibitions
      const loggedInExhibitions = storageService.getAllLoggedInExhibitions();
      const exhibitorAuthData = {};
      
      // For each logged in exhibition, get the user data
      Object.keys(loggedInExhibitions).forEach(exhibitionId => {
        const userData = storageService.getExhibitionUserData(exhibitionId);
        if (userData && userData.token) {
          exhibitorAuthData[exhibitionId] = userData;
        }
      });
      
      setExhibitorAuth(exhibitorAuthData);
      
      // Load visitor auth and data using storageService
      const visitorAuthData = storageService.getItem('visitorAuth');
      if (visitorAuthData) {
        setVisitorAuth(visitorAuthData);
      }
      
      const visitorProfileData = storageService.getVisitorData();
      if (visitorProfileData) {
        setVisitorData(visitorProfileData);
      }
    } catch (error) {
      console.error('Error loading auth data:', error);
    }
  }, []);
  
  // Check if logged in to a specific exhibition
  const isLoggedInToExhibition = (exhibitionId) => {
    if (!exhibitionId) return false;
    return storageService.getExhibitionUserData(exhibitionId) !== null;
  };
  
  // Check if logged in as visitor
  const isLoggedInAsVisitor = () => {
    return storageService.getVisitorData() !== null;
  };
  
  // Log in to an exhibition as exhibitor
  const loginToExhibition = (exhibitionId, userData) => {
    if (!exhibitionId || !userData) return false;
    
    try {
      // Store using storageService
      const success = storageService.setExhibitionUserData(exhibitionId, userData);
      
      if (success) {
        // Update local state
        const updatedAuth = {
          ...exhibitorAuth,
          [exhibitionId]: userData
        };
        setExhibitorAuth(updatedAuth);
        
        console.log(`✅ Logged in to exhibition ${exhibitionId}`);
        return true;
      }
      
      return false;
    } catch (error) {
      console.error('Error logging in to exhibition:', error);
      return false;
    }
  };
  
  // Log in as visitor
  const loginAsVisitor = (userData) => {
    try {
      const authData = {
        isAuthenticated: true,
        timestamp: new Date().toISOString(),
      };
      
      // Store using storageService
      const authSuccess = storageService.setItem('visitorAuth', authData);
      const dataSuccess = storageService.setVisitorData(userData);
      
      if (authSuccess && dataSuccess) {
        setVisitorAuth(authData);
        setVisitorData(userData);
        
        console.log('✅ Logged in as visitor');
        return true;
      }
      
      return false;
    } catch (error) {
      console.error('Error logging in as visitor:', error);
      return false;
    }
  };
  
  // Log out from an exhibition as exhibitor
  const logoutFromExhibition = (exhibitionId) => {
    if (!exhibitionId) return false;
    
    try {
      // Remove using storageService
      const success = storageService.removeExhibitionUserData(exhibitionId);
      
      if (success) {
        // Update local state
        const updatedAuth = { ...exhibitorAuth };
        delete updatedAuth[exhibitionId];
        setExhibitorAuth(updatedAuth);
        
        console.log(`🚪 Logged out from exhibition ${exhibitionId}`);
        return true;
      }
      
      return false;
    } catch (error) {
      console.error('Error logging out from exhibition:', error);
      return false;
    }
  };
  
  // Log out as visitor
  const logoutAsVisitor = () => {
    try {
      // Remove using storageService
      const authSuccess = storageService.removeItem('visitorAuth');
      const dataSuccess = storageService.removeVisitorData();
      
      if (authSuccess && dataSuccess) {
        setVisitorAuth(null);
        setVisitorData(null);
        
        console.log('🚪 Logged out as visitor');
        return true;
      }
      
      return false;
    } catch (error) {
      console.error('Error logging out as visitor:', error);
      return false;
    }
  };
  
  // Switch current exhibition context
  const switchExhibition = (exhibitionId) => {
    if (!exhibitionId) return false;
    
    try {
      // Verify the exhibition is logged in
      const userData = storageService.getExhibitionUserData(exhibitionId);
      
      if (userData) {
        console.log(`🔄 Switched to exhibition: ${exhibitionId}`);
        return true;
      } else {
        console.warn(`⚠️ Cannot switch to exhibition ${exhibitionId} - not logged in`);
        return false;
      }
    } catch (error) {
      console.error('Error switching exhibition:', error);
      return false;
    }
  };
  
  // Update visitor profile
  const updateVisitorProfile = (userData) => {
    try {
      const currentData = storageService.getVisitorData() || {};
      const updatedData = {
        ...currentData,
        ...userData,
        updatedAt: new Date().toISOString()
      };
      
      const success = storageService.setVisitorData(updatedData);
      
      if (success) {
        setVisitorData(updatedData);
        console.log('✅ Visitor profile updated');
        return true;
      }
      
      return false;
    } catch (error) {
      console.error('Error updating visitor profile:', error);
      return false;
    }
  };
  
  // Get current user info for an exhibition
  const getExhibitionUserData = (exhibitionId) => {
    return storageService.getExhibitionUserData(exhibitionId);
  };
  
  // Get all logged in exhibitions
  const getAllLoggedInExhibitions = () => {
    return storageService.getAllLoggedInExhibitions();
  };
  
  // Clear all auth data (for debugging or full logout)
  const clearAllAuthData = () => {
    try {
      // Get all logged in exhibitions and clear them
      const loggedInExhibitions = storageService.getAllLoggedInExhibitions();
      Object.keys(loggedInExhibitions).forEach(exhibitionId => {
        storageService.removeExhibitionUserData(exhibitionId);
      });
      
      // Clear visitor data
      storageService.removeItem('visitorAuth');
      storageService.removeVisitorData();
      
      // Clear profile ID
      storageService.removeProfileID();
      
      // Clear any cached data
      storageService.clearAllCaches();
      
      // Reset local state
      setExhibitorAuth({});
      setVisitorAuth(null);
      setVisitorData(null);
      
      console.log('🧹 All auth data cleared');
      return true;
    } catch (error) {
      console.error('Error clearing auth data:', error);
      return false;
    }
  };
  
  const value = {
    // Exhibitor auth
    isLoggedInToExhibition,
    loginToExhibition,
    logoutFromExhibition,
    switchExhibition,
    exhibitorAuth,
    getExhibitionUserData,
    getAllLoggedInExhibitions,
    
    // Visitor auth
    isLoggedInAsVisitor,
    loginAsVisitor,
    logoutAsVisitor,
    visitorData,
    updateVisitorProfile,
    
    // Utility
    clearAllAuthData
  };
  
  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};